import React, {useMemo, useState} from "react";
import {useRef} from "react";
import {useNavigation} from "@react-navigation/native";
import {Text,Animated, TouchableOpacity,StyleSheet, View} from "react-native";
import WebPageLayout from "../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../layouts/TablePageLayout";
import {CoreTable} from "../../../../components/CoreTable";
import {DataTable, Button} from "react-native-paper";
import {Button as AntButton, Tooltip,Modal, Checkbox} from "antd";
import {Feather, MaterialIcons} from "@expo/vector-icons";
import {AlignCenterOutlined, ExportOutlined, FilePdfOutlined, PlusOutlined} from "@ant-design/icons";
import {BaseFilters} from "../../../../../web/hooks/useFilters";
import useEntityListPage from "../../../../hooks/useEntityListPage";
import {
  useDeleteWorkOrderMutation,
  useLazyExportWorkOrdersQuery,
  useLazyGetWorkOrdersQuery,
  useSetWorkOrderStatusMutation
} from "../../../../../../store/services/api/work-order-api";
import {
  WorkOrder,
  WorkOrderPriority,
  WorkOrderStatus,
  WorkOrderType
} from "../../../../../../store/data/entities/work-order";
import WorkOrdersListScreenFilters from "./WorkOrdersListScreenFilters";
import {
  humanizeDate,
  humanizeEnum,
  humanizePastDate,
  humanizeWorkOrderPriority
} from "../../../../../../utils/humanizers";
import useHasPermission from "../../../../../../hooks/useHasPermission";
import ConfirmModal from "../../../../../../components/modals/New/ConfirmModal";
import {useReport} from "../../../../hooks/useReport";
import {useLazyGetWorkOrderReportQuery} from "../../../../../../store/services/api/report-api";
import {useExcelExport} from "../../../../hooks/useExport";
import {Basket, BasketIcon} from "../../../../components/Basket";
import useBasket, {BasketItem} from "../../../../../../hooks/useBasket";
import { DateTime } from "luxon";
import ColumnExportModal, { ExportableColumn } from "../../../../../../components/modals/ColumnExportModal";
import WorkOrdersListAssetModal from "./workOrdersListAssetModal";


interface WorkOrderListFilters extends BaseFilters {
  reference?: string;
  parentReference?: string;
  description?: string;
  code?: string;
  priority?: WorkOrderPriority[];
  statuses?: WorkOrderStatus[];
  startDate?: string;
  endDate?: string;
  buildingLeadId?: string[];
  createdByUserId?: string[];
  createdOnStartDate?: string;
  createdOnEndDate?: string;
  type?: WorkOrderType;
  technicianId?: string[];
  closedOnStartDate?: string;
  closedOnEndDate?: string;
  labourEntryStartDate?: string;
  labourEntryEndDate?: string;
  closedByUserId?: string[];
  scheduledFinishStartDate?: string;
  scheduledFinishEndDate?: string;
}

export default function WorkOrdersList() {
  const [basket] = useBasket();

  const navigation = useNavigation<any>();
  const {hasPermission} = useHasPermission();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [setWorkOrderStatus, {isLoading: workOrderStatusLoading}] = useSetWorkOrderStatusMutation();
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | undefined>();
  const {isLoading: reportLoading, downloadReport} = useReport({
    query: useLazyGetWorkOrderReportQuery
  })
  const {exportExcel, isLoading: exportLoading} = useExcelExport({
    query: useLazyExportWorkOrdersQuery
  });


  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const exportableColumns: ExportableColumn[] = [
    { key: "Reference", label: "Reference" },
    { key: "ParentWorkOrderReference", label: "Parent WO" },
    { key: "TicketNumber", label: "Ticket" },
    { key: "PriorityLabel", label: "Priority" },
    { key: "FormattedScheduledStartDate", label: "Scheduled Date" },
    { key: "FormattedScheduledFinishDate", label: "Must Finish By" },
    { key: "FormattedActualStartDate", label: "Actual Start Date" },
    { key: "FormattedActualFinishDate", label: "Actual Finish Date" },
    { key: "FormattedCreatedOn", label: "Created On" },
    { key: "FormattedClosedOn", label: "Closed On" },
    { key: "WorkOrderAge", label: "WO Age" },
    { key: "StatusLabel", label: "Status" },
    { key: "TypeLabel", label: "Type" },
    { key: "LocationLabel", label: "Location" },
    { key: "Description", label: "Description" },
    { key: "BuildingLead", label: "Owner" },
    { key: "CreatedBy", label: "Created By" },
    { key: "ClosedBy", label: "Closed By" },
    { key: "Technician", label: "Technician" },
    { key: "TotalLabourHoursFormatted", label: "Total Labour Hours" },
  ];

  const reopenWorkOrder = () => {
    if (!selectedWorkOrderId) return;
    setWorkOrderStatus({
      workOrderId: selectedWorkOrderId,
      status: WorkOrderStatus.Approved
    }).unwrap()
      .then(() => {
        setSelectedWorkOrderId(undefined)
        setConfirmModalOpen(false);
      })
  }

  const handleOnWorkOrderReopen = (id: string) => {
    setSelectedWorkOrderId(id);
    setConfirmModalOpen(true);
  }

  const {
    navigateToEdit,
    navigateToCreate,
    navigateToApproval,
    onSubmitFilter,
    filter,
    tableData,
    isLoading
  } = useEntityListPage<WorkOrderListFilters>({
    entityName: "Work Order",
    lazyTableQuery: useLazyGetWorkOrdersQuery,
    deleteEntityMutation: useDeleteWorkOrderMutation,
    filterDefinition: {
      sortedColumn: 'referenceNumber',
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50,
      statuses: [
        WorkOrderStatus.PendingApproval,
        WorkOrderStatus.Approved,
        WorkOrderStatus.InProgress,
        WorkOrderStatus.Completed
      ]
    }
  });

  const navigateToRecordView = (id: string) => navigation.navigate('recordView', {id});
  const navigateToTicketList = (ticketNumber: string | number) => {
    navigation.navigate('tickets', {
      screen: 'list',
      params: {
        ticketNumber
      },
    });
  };

  const isAllSelected = useMemo(() => {
    const applicableItems = tableData?.list?.filter(item => item.isApproved) ?? [];
    if (applicableItems.length === 0) {
      return false;
    }

    return applicableItems.every((item) => {
      return basket.selectedItems.some(selectedItem => selectedItem?.id === item?.id);
    });
  }, [basket.selectedItems, tableData]);

  const addSelectedPageToBasket = async () => {
    const list: BasketItem[] = tableData?.list.filter(w => w.isApproved).map((workOrder: WorkOrder) => ({
      id: workOrder.id,
      label: workOrder.reference,
    })) ?? [];
    basket.addItems(list);
  }

  const removeSelectedPageFromBasket = async () => {
    const itemsToRemove: string[] = tableData?.list.map((workOrder: WorkOrder) => workOrder.id) ?? [];
    basket.removeItems(itemsToRemove);
  };

  const getRowColour = (item: WorkOrder, rowIndex: number) => {
    const rowColour = 'rgba(255,255,255,1)';
    const alternateRowColour = 'rgba(245,245,245,1)';
    const warnColour = 'rgb(255, 229, 143)';
    const dangerColour = 'rgb(255, 204, 199)';

    if (!item.scheduledFinishDate
      || item.status === WorkOrderStatus.Completed
      || item.status === WorkOrderStatus.Cancelled
      || item.status === WorkOrderStatus.Closed
    ) {
      return rowIndex % 2 === 0 ? alternateRowColour : rowColour;
    }

    const now = DateTime.now().startOf('day');
    const finishDate = DateTime.fromISO(item.scheduledFinishDate)
      .toUTC()
      .setZone(DateTime.local().zoneName)
      .startOf('day');

    if (finishDate.hasSame(now, 'day')) {
      return warnColour;
    }

    if (finishDate < now) {
      return dangerColour;
    }

    return rowIndex % 2 === 0 ? alternateRowColour : rowColour;
  }


   const [filtersVisible, setFiltersVisible] = useState(true);
    const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;
  
    const toggleFilters = () => {
      const toValue = filtersVisible ? 0 : 1;
      Animated.timing(filtersWidth, {
        toValue,
        duration: 300,
        useNativeDriver: false,
      }).start();
      setFiltersVisible(!filtersVisible);
    };

  return (
    <WebPageLayout
      header={{
        previousTab: {title: 'Dashboard', routeName: 'dashboard', replaceStack: true},
        tabs: [
          {title: 'Work Orders', routeName: 'list'}
        ]
      }}
    >
      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: "hidden",
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], // adjust width as needed
              }),
              height: "100%",
            }}
          >
            <WorkOrdersListScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }
        actions={(
          <>
            <Button
              icon={filtersVisible ? "chevron-left" : "chevron-right"}
              mode="contained-tonal"
              onPress={toggleFilters}
              style={{ marginRight: 8 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>
            <Basket basket={basket}/>
            <AntButton
              size='large'
              icon={<PlusOutlined/>}
              onClick={navigateToCreate}
            >
              Create Work Order
            </AntButton>
            <AntButton
              size='large'
              icon={<ExportOutlined />}
              onClick={() => setExportModalOpen(true)}
            >
              Export Excel
            </AntButton>
          </>
        )}
      >
        <CoreTable<WorkOrder>
  isLoading={isLoading || reportLoading || exportLoading}
  headers={[
    { text: 'Basket', hidden: false, flex: 0.3 },
    { text: 'PDF', hidden: false, flex: 0.3 },
    { text: 'Reference', sortedName: 'referenceNumber',flex: 0.7},
    { text: 'Parent WO', sortedName: 'parentWorkOrder.referenceNumber',flex: 0.7 },
    { text: 'Ticket', sortedName: 'ticket.number' ,flex: 0.6},
    { text: 'Priority', sortedName: 'priority' ,flex: 0.6},
    { text: 'Scheduled Date', sortedName: 'scheduledStartDate',flex: 0.6 },
    { text: 'Must Finish By', sortedName: 'scheduledFinishDate' ,flex: 0.6},
    { text: 'WO Age' ,flex: 0.5},
    { text: 'Type', sortedName: 'type', flex: 0.7  },
    { text: 'Location', sortedName: 'locationLabel' },
    { text: 'Description', sortedName: 'description' },
    { text: 'Status', sortedName: 'status' },
    { text: 'Total Labour Hours', sortedName: 'totalLabourHours' },
    { text: 'Options' }
  ]}
  showCheckbox={true}
  isSelectAllChecked={isAllSelected}
  onSelectAll={addSelectedPageToBasket}
  onClearAll={removeSelectedPageFromBasket}
  data={tableData}
  keyExtractor={(item) => item.id}
  renderItem={({ item, index, headers }) => {
    const showApprovalButton =
      hasPermission('CanApproveWorkOrders') && item.status === WorkOrderStatus.PendingApproval;
    const showReopenButton =
      hasPermission('CanReopenWorkOrders') && item.status === WorkOrderStatus.Closed;
    const showEditButton =
      hasPermission('CanCreateWorkOrders') &&
      !(item.status === WorkOrderStatus.Cancelled || item.status === WorkOrderStatus.Closed);
    const canEditRecord = item.status !== WorkOrderStatus.Cancelled && item.status !== WorkOrderStatus.Rejected;

    return (
      <CoreTable.Row key={index} style={{ backgroundColor: getRowColour(item, index) }}>
        {headers.map((header, i) => {
          switch (header.text) {
            case 'Basket':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }} tooltip={false}>
                  {item.isApproved && (
                    <BasketIcon
                      isSelected={basket.isItemSelected(item.id)}
                      onToggle={() =>
                        basket.toggleItem({ id: item.id, label: item.reference })
                      }
                    />
                  )}
                </CoreTable.Cell>
              );
            case 'PDF':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {item.isApproved && (
                    <AntButton
                      onClick={() => downloadReport(item.id)}
                      icon={<FilePdfOutlined />}
                      shape="circle"
                      type="text"
                    />
                  )}
                </CoreTable.Cell>
              );
            case 'Reference':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {item.hasAssets ? (
                    <WorkOrdersListAssetModal workOrderId={item.id} triggerContent={item.reference} />
                  ) : (
                    item.reference
                  )}
                </CoreTable.Cell>
              );
            case 'Parent WO':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {item?.parentWorkOrderId ? (
                    <Text
                      onPress={() => navigateToRecordView(item.parentWorkOrderId)}
                      style={{ fontWeight: 'bold' }}
                    >
                      {item?.parentWorkOrderReference ?? 'Unknown'}
                    </Text>
                  ) : (
                    <Text>N/A</Text>
                  )}
                </CoreTable.Cell>
              );
            case 'Ticket':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {!item.ticketNumber ? 'N/A' : (
                    <Text
                      onPress={() => navigateToTicketList(item.ticketNumber)}
                      style={{ fontWeight: 'bold' }}
                    >
                      {item.ticketNumber}
                    </Text>
                  )}
                </CoreTable.Cell>
              );
            case 'Priority':
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{humanizeWorkOrderPriority(item.priority)}</CoreTable.Cell>;
            case 'Scheduled Date':
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{humanizeDate(item?.scheduledStartDate)}</CoreTable.Cell>;
            case 'Must Finish By':
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{humanizeDate(item?.scheduledFinishDate)}</CoreTable.Cell>;
            case 'WO Age':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {item.status === WorkOrderStatus.Approved || item.status === WorkOrderStatus.InProgress
                    ? humanizePastDate(item?.scheduledStartDate)
                    : 'N/A'}
                </CoreTable.Cell>
              );
            case 'Type':
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{humanizeEnum(WorkOrderType[item?.type])}</CoreTable.Cell>;
            case 'Location':
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{item.locationLabel ?? 'N/A'}</CoreTable.Cell>;
            case 'Description':
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{item.description}</CoreTable.Cell>;
            case 'Status':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {item.isRejected ? (
                    <Tooltip title={item.rejectedReason ?? 'No reason provided'}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <Text>Rejected</Text>
                        <Feather name="alert-circle" size={16} color="#fab005" />
                      </View>
                    </Tooltip>
                  ) : (
                    humanizeEnum(WorkOrderStatus[item.status])
                  )}
                </CoreTable.Cell>
              );
            case 'Total Labour Hours':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {item.totalLabourHours}
                </CoreTable.Cell>
              );
            case 'Options':
              return (
                <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                  {canEditRecord && (
                    <Tooltip title="View Record">
                      <TouchableOpacity onPress={() => navigateToRecordView(item.id)} style={{ marginRight: 4 }}>
                        <MaterialIcons name="login" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                  {showApprovalButton && (
                    <Tooltip title="View for Approval">
                      <TouchableOpacity onPress={() => navigateToApproval(item.id)} style={{ marginRight: 4 }}>
                        <MaterialIcons name="approval" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                  {showReopenButton && (
                    <Tooltip title="Reopen Work Order">
                      <TouchableOpacity onPress={() => handleOnWorkOrderReopen(item.id)} style={{ marginRight: 4 }}>
                        <MaterialIcons name="undo" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                  {showEditButton && (
                    <Tooltip title="Edit Work Order">
                      <TouchableOpacity onPress={() => navigateToEdit(item.id)} style={{ marginRight: 4 }}>
                        <MaterialIcons name="edit" size={24} color="black" />
                      </TouchableOpacity>
                    </Tooltip>
                  )}
                </CoreTable.Cell>
              );
            default:
              return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>{/* fallback empty */}</CoreTable.Cell>;
          }
        })}
      </CoreTable.Row>
    );
  }}
  filters={filter.filters}
  setFilter={filter.setFilter}
/>


        <ConfirmModal
          title='Reopen Work Order'
          message='Are you sure you want to reopen this work order?'
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={reopenWorkOrder}
          isLoading={workOrderStatusLoading}
        />

        <ColumnExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          onConfirm={(cols) => {
            exportExcel({
              ...filter.getFilterValues(),
              columns: cols,
            });
            setExportModalOpen(false);
          }}
          columns={exportableColumns}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
        />

      </TablePageLayout>
    </WebPageLayout>
  );
}
const styles = StyleSheet.create({

  header: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "flex-start",
    paddingHorizontal: 0
  },

});